import React from 'react';
import { Route } from 'react-router';

import CoachingChatStreamContainer from 'coaching/CoachingChatStreamContainer';
import CoachingConstants from 'coaching/CoachingConstants';
import CoachingCoordinatorContainer from 'coaching/CoachingCoordinatorContainer';
import CoachingSelfServeContainer from 'coaching/CoachingSelfServeContainer';
import TutoringSelfServeContainer from 'coaching/TutoringSelfServeContainer';
import CoachingHomeContainer from 'coaching/CoachingHomeContainer';
import CoachingHelpContainer from 'coaching/CoachingHelpContainer';
import CoachingHelpFormContainer from 'coaching/CoachingHelpFormContainer';
import CoachingCoachHelpContainer from 'coaching/CoachingCoachHelpContainer';
import CoachingCoachHelpFormContainer from 'coaching/CoachingCoachHelpFormContainer';
import chainOnEnter from 'components/lib/chainOnEnter';
import EventTracking from 'lib/EventTracking';
// @ts-ignore
import RequireUser from 'lib/RequireUser';
// @ts-ignore
import RequireUnauth from 'lib/RequireUnauth';
// @ts-ignore
import RequireSetup from 'lib/RequireSetup';
import RequireLoginWithAuthToken from 'lib/RequireLoginWithAuthToken';
import Cookies from 'lib/Cookies';
import AcceptCoachingInvitation from 'coaching/components/AcceptCoachingInvitation';
import { redirectIfMobile, redirectIfDesktop } from 'coaching/lib/deviceType';
import CoachDashboard from 'coaching/components/CoachDashboard';
// @ts-ignore
import AuthActions from 'auth/AuthActions';
import CurrentUser from 'auth/CurrentUser';

// Flag to indicate if we're already fetched the user data from api.
// CurrentUser keeps the user information in-process, so it makes sense to keep this flag in-process as well
let userDataAlreadyFetched = false;

const fetchUserDataIfFirstTime = async () => {
  if (CurrentUser.isSignedIn() || userDataAlreadyFetched) {
    return Promise.resolve();
  }
  userDataAlreadyFetched = true;
  return AuthActions.checkLoggedInAndLoadAppData()
    .then(() => {
      console.info('User is authenticated');
    })
    .catch(() => {
      console.info('User is not authenticated');
    });
};

export const CoachingIsolatedRoutes = (
  <>
    <React.Fragment>
      <Route
        path={CoachingConstants.TUTORING_STUDENT_DETAILS}
        component={TutoringSelfServeContainer}
        onEnter={RequireUnauth({ location: CoachingConstants.TUTORING_GET_STARTED })}
      />
      <Route
        path={CoachingConstants.TUTORING_GET_STARTED}
        component={TutoringSelfServeContainer}
        onEnter={chainOnEnter([fetchUserDataIfFirstTime])}
      />
      <Route
        path={CoachingConstants.TUTORING_SCHEDULE}
        component={TutoringSelfServeContainer}
        onEnter={chainOnEnter([fetchUserDataIfFirstTime])}
      />
      <Route
        path={CoachingConstants.TUTORING_BILLING}
        component={TutoringSelfServeContainer}
        onEnter={chainOnEnter([fetchUserDataIfFirstTime])}
      />
      <Route
        path={CoachingConstants.TUTORING_LINK_STUDENT}
        component={TutoringSelfServeContainer}
        onEnter={chainOnEnter([fetchUserDataIfFirstTime])}
      />
      <Route
        path={CoachingConstants.TUTORING_SUMMARY}
        component={TutoringSelfServeContainer}
        onEnter={chainOnEnter([fetchUserDataIfFirstTime])}
      />
    </React.Fragment>
    <Route
      path={CoachingConstants.COACHING_PURCHASE_REVIEW_ROUTE}
      component={CoachingSelfServeContainer} // TODO: Remove - should never actually render
      onEnter={(_nextState, replace) => {
        // This isn't a good place to do event tracking but having this event here will give
        // us behavior that is closest to the existing behavior for this event in CoachingSelfServeView
        EventTracking.trackKey('signup_experiment.experiment_start.start_new_signup');
        // Expire the cookie since the user is being redirected the new sign up flow
        // This cookie expiration can be removed when it's no longer looked at by the sign in page
        Cookies.expire(CoachingConstants.IS_INTENDED_COACHING_USER_COOKIE);
        replace({ pathname: CoachingConstants.TUTORING_GET_STARTED });
      }}
    />
    <Route
      path={CoachingConstants.COACHING_COORDINATOR_ROUTE}
      component={CoachingCoordinatorContainer}
      onEnter={RequireUser}
    />
    <Route
      path={CoachingConstants.COACHING_ACCEPT_INVITATION_ROUTE}
      onEnter={chainOnEnter([RequireLoginWithAuthToken, RequireUser, RequireSetup])}
      component={AcceptCoachingInvitation}
    />
    {/* No left nav bar on mobile home */}
    <Route
      path={CoachingConstants.COACHING_HOME_MOBILE_ROUTE}
      component={CoachingHomeContainer}
      onEnter={chainOnEnter([
        redirectIfDesktop(CoachingConstants.COACHING_HOME_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_BILLING_MOBILE_ROUTE}
      component={CoachingHomeContainer}
      onEnter={chainOnEnter([
        redirectIfDesktop(CoachingConstants.COACHING_BILLING_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_SCHEDULE_MOBILE_ROUTE}
      component={CoachDashboard}
      onEnter={chainOnEnter([
        redirectIfDesktop(CoachingConstants.COACHING_SCHEDULE_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_AVAILABILITY_MOBILE_ROUTE}
      component={CoachDashboard}
      onEnter={chainOnEnter([
        redirectIfDesktop(CoachingConstants.COACHING_AVAILABILITY_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.TUTORING_HELP_FORM_ROUTE}
      component={CoachingHelpFormContainer}
    />
    <Route
      path={CoachingConstants.TUTORING_COACH_HELP_FORM_ROUTE}
      component={CoachingCoachHelpFormContainer}
    />
  </>
);

export const CoachingRoutes = (
  <>
    <Route
      path={CoachingConstants.COACHING_CHAT_STREAM_ROUTE}
      component={CoachingChatStreamContainer}
      onEnter={RequireUser}
    />
    <Route
      path={CoachingConstants.COACHING_SCHEDULE_ROUTE}
      component={CoachDashboard}
      onEnter={chainOnEnter([
        redirectIfMobile(CoachingConstants.COACHING_SCHEDULE_MOBILE_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_AVAILABILITY_ROUTE}
      component={CoachDashboard}
      onEnter={chainOnEnter([
        redirectIfMobile(CoachingConstants.COACHING_AVAILABILITY_MOBILE_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_HOME_ROUTE}
      component={CoachingHomeContainer}
      onEnter={chainOnEnter([
        redirectIfMobile(CoachingConstants.COACHING_HOME_MOBILE_ROUTE),
        RequireUser,
      ])}
    />
    <Route
      path={CoachingConstants.COACHING_BILLING_ROUTE}
      component={CoachingHomeContainer}
      onEnter={chainOnEnter([
        redirectIfMobile(CoachingConstants.COACHING_BILLING_MOBILE_ROUTE),
        RequireUser,
      ])}
    />
  </>
);
